import type { CSSProperties } from 'react';
import { adminTokens as t, type AdminStatusTone } from './adminTokens';

/**
 * Spread into every interactive admin control. It keeps the browser's
 * focus-visible ring (admin controls never set `outline: none`) and draws it in
 * the admin focus color, 2 px away from the control so it stays visible next to
 * dark fills.
 */
export const adminFocusable: CSSProperties = {
  outlineColor: t.focus.ring,
  outlineOffset: '2px',
};

export const adminText = {
  pageTitle: {
    margin: 0,
    fontSize: t.typography.pageTitle.size,
    lineHeight: t.typography.pageTitle.lineHeight,
    fontWeight: t.typography.pageTitle.weight,
    color: t.text.primary,
  },
  sectionTitle: {
    margin: 0,
    fontSize: t.typography.sectionTitle.size,
    lineHeight: t.typography.sectionTitle.lineHeight,
    fontWeight: t.typography.sectionTitle.weight,
    color: t.text.primary,
  },
  body: {
    margin: 0,
    fontSize: t.typography.body.size,
    lineHeight: t.typography.body.lineHeight,
    color: t.text.primary,
  },
  secondary: {
    margin: 0,
    fontSize: t.typography.body.size,
    lineHeight: t.typography.body.lineHeight,
    color: t.text.secondary,
  },
  label: {
    fontSize: t.typography.label.size,
    lineHeight: t.typography.label.lineHeight,
    fontWeight: 600,
    color: t.text.secondary,
  },
} satisfies Record<string, CSSProperties>;

const buttonBase: CSSProperties = {
  ...adminFocusable,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: t.spacing.sm,
  minHeight: '36px',
  padding: `0 ${t.spacing.lg}`,
  borderRadius: t.radius.control,
  fontFamily: 'inherit',
  fontSize: t.typography.body.size,
  fontWeight: 600,
  lineHeight: 1.2,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
};

export const adminButton = {
  primary: {
    ...buttonBase,
    border: `1px solid ${t.action.primary}`,
    backgroundColor: t.action.primary,
    color: t.text.onPrimary,
  },
  secondary: {
    ...buttonBase,
    border: `1px solid ${t.border.control}`,
    backgroundColor: t.surface.panel,
    color: t.text.primary,
  },
  /** Destructive confirmation; its label always names the action. */
  danger: {
    ...buttonBase,
    border: `1px solid ${t.status.error.text}`,
    backgroundColor: t.status.error.text,
    color: t.text.onPrimary,
  },
  /** Text-only action such as "Reset" or "Clear filters". */
  link: {
    ...adminFocusable,
    padding: 0,
    border: 'none',
    background: 'none',
    fontFamily: 'inherit',
    fontSize: t.typography.label.size,
    fontWeight: 600,
    color: t.text.link,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  /** Square icon-only control; callers must give it an aria-label. */
  icon: {
    ...adminFocusable,
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    borderRadius: t.radius.control,
    border: `1px solid ${t.border.control}`,
    backgroundColor: t.surface.panel,
    color: t.text.primary,
    cursor: 'pointer',
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
} satisfies Record<string, CSSProperties>;

export const adminField = {
  input: {
    ...adminFocusable,
    height: '38px',
    padding: `0 ${t.spacing.md}`,
    border: `1px solid ${t.border.control}`,
    borderRadius: t.radius.control,
    backgroundColor: t.surface.panel,
    color: t.text.primary,
    fontFamily: 'inherit',
    fontSize: t.typography.body.size,
    boxSizing: 'border-box',
  },
  select: {
    ...adminFocusable,
    height: '38px',
    padding: `0 ${t.spacing.sm}`,
    border: `1px solid ${t.border.control}`,
    borderRadius: t.radius.control,
    backgroundColor: t.surface.panel,
    color: t.text.primary,
    fontFamily: 'inherit',
    fontSize: t.typography.body.size,
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  label: {
    display: 'block',
    marginBottom: t.spacing.xs,
    fontSize: t.typography.label.size,
    fontWeight: 600,
    color: t.text.primary,
  },
  hint: {
    margin: `${t.spacing.xs} 0 0`,
    fontSize: t.typography.label.size,
    lineHeight: t.typography.label.lineHeight,
    color: t.text.secondary,
  },
} satisfies Record<string, CSSProperties>;

export const adminSurface = {
  /** Management-page frame: tinted canvas filling the shell's content pane. */
  page: {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: t.spacing.lg,
    padding: `${t.spacing.xl} ${t.layout.pageGutter}`,
    backgroundColor: t.surface.canvas,
    color: t.text.primary,
    boxSizing: 'border-box',
  },
  pageNarrow: {
    padding: `${t.spacing.lg} ${t.layout.pageGutterNarrow}`,
  },
  panel: {
    backgroundColor: t.surface.panel,
    border: `1px solid ${t.border.subtle}`,
    borderRadius: t.radius.panel,
    boxShadow: t.shadow.panel,
  },
  panelPadded: {
    padding: `${t.spacing.lg} ${t.spacing.xl}`,
  },
} satisfies Record<string, CSSProperties>;

export function adminStatusStyle(tone: AdminStatusTone): CSSProperties {
  const palette = t.status[tone];
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: t.spacing.sm,
    margin: 0,
    padding: `${t.spacing.sm} ${t.spacing.md}`,
    border: `1px solid ${palette.border}`,
    borderRadius: t.radius.control,
    backgroundColor: palette.background,
    color: palette.text,
    fontSize: t.typography.body.size,
    lineHeight: t.typography.body.lineHeight,
  };
}

/**
 * Right-edge panels opened from list rows (lesson and question usage, account
 * assignment). Callers add their own position, size and open/closed motion.
 */
export const adminSidePanel = {
  scrim: {
    position: 'absolute',
    inset: 0,
    backgroundColor: t.surface.scrim,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: t.surface.panel,
    color: t.text.primary,
    borderLeft: `1px solid ${t.border.subtle}`,
    boxShadow: t.shadow.overlay,
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: t.spacing.md,
    padding: `${t.spacing.lg} ${t.spacing.xl}`,
    background: t.surface.headerWash,
    borderBottom: `1px solid ${t.border.subtle}`,
  },
  headerText: { minWidth: 0 },
  title: adminText.sectionTitle,
  subtitle: {
    margin: `${t.spacing.xs} 0 0`,
    fontSize: t.typography.label.size,
    lineHeight: t.typography.label.lineHeight,
    color: t.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  closeButton: {
    ...adminFocusable,
    width: '32px',
    height: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
    border: 'none',
    borderRadius: t.radius.control,
    background: 'none',
    color: t.text.secondary,
    fontSize: '22px',
    lineHeight: 1,
    cursor: 'pointer',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: t.spacing.md,
    padding: `${t.spacing.md} ${t.spacing.xl}`,
    borderBottom: `1px solid ${t.border.subtle}`,
  },
  rowText: { minWidth: 0 },
  rowLink: {
    ...adminFocusable,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: t.typography.body.size,
    fontWeight: 600,
    color: t.text.primary,
    textDecoration: 'none',
  },
  rowMeta: {
    margin: '2px 0 0',
    fontSize: t.typography.caption.size,
    lineHeight: t.typography.caption.lineHeight,
    color: t.text.secondary,
  },
  stateText: {
    margin: 0,
    padding: `${t.spacing.lg} ${t.spacing.xl}`,
    fontSize: t.typography.body.size,
    lineHeight: t.typography.body.lineHeight,
    color: t.text.secondary,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: t.spacing.md,
    padding: `${t.spacing.md} ${t.spacing.xl}`,
    backgroundColor: t.surface.canvas,
    borderTop: `1px solid ${t.border.subtle}`,
  },
  footerNote: {
    margin: 0,
    fontSize: t.typography.caption.size,
    lineHeight: t.typography.caption.lineHeight,
    color: t.text.secondary,
  },
  footerCount: {
    flexShrink: 0,
    fontSize: t.typography.label.size,
    fontWeight: 600,
    color: t.text.secondary,
  },
} satisfies Record<string, CSSProperties>;

/**
 * Page, table and row-menu recipes shared by the management lists (Lessons,
 * Courses, Maps, Questions, Tags, Questionnaires). Same keys as the list
 * styles they replace, so `SortableHeader` and the row markup are unchanged.
 */
export type AdminListStyleKey =
  | 'page'
  | 'stickyTop'
  | 'pageHeader'
  | 'pageTitle'
  | 'titleBlock'
  | 'pageDescription'
  | 'cardList'
  | 'card'
  | 'cardTitle'
  | 'cardSubtitle'
  | 'cardActions'
  | 'primaryButton'
  | 'addLessonButton'
  | 'listToolbar'
  | 'filterInput'
  | 'resultsCount'
  | 'resetFiltersButton'
  | 'panel'
  | 'tableScroll'
  | 'stateText'
  | 'table'
  | 'headerCell'
  | 'headerCellSortable'
  | 'headerCellLabel'
  | 'sortIndicator'
  | 'sortIndicatorInactive'
  | 'headerCellActions'
  | 'tableRow'
  | 'idCell'
  | 'bodyCell'
  | 'titleButton'
  | 'standardTitleLine'
  | 'accountLinkButton'
  | 'actionsCell'
  | 'rowActionButton'
  | 'rowActionButtonDanger'
  | 'rowActions'
  | 'actionMenuWrapper'
  | 'actionMenuButton'
  | 'actionMenuPopover'
  | 'actionMenuItem'
  | 'actionMenuItemDisabled'
  | 'actionMenuItemDanger'
  | 'actionMenuItemReason'
  | 'modalOverlay'
  | 'modal'
  | 'modalTitle'
  | 'modalText'
  | 'modalActions'
  | 'disclosureButton'
  | 'cancelButton'
  | 'confirmDeleteButton'
  | 'confirmActionButton'
  | 'confirmDeleteButtonDisabled';

export type AdminListStyles = Record<AdminListStyleKey, CSSProperties>;

export function createAdminListStyles({ narrow = false }: { narrow?: boolean } = {}): AdminListStyles {
  const pageGutter = narrow ? t.layout.pageGutterNarrow : t.layout.pageGutter;
  const pageTop = narrow ? t.spacing.lg : t.spacing.xl;
  return {
    page: {
      ...adminSurface.page,
      padding: `${pageTop} ${pageGutter}`,
      minWidth: 0,
    },
    stickyTop: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: t.spacing.md,
      paddingTop: pageTop,
      paddingBottom: t.spacing.sm,
      marginTop: `calc(-1 * ${pageTop})`,
      backgroundColor: t.surface.canvas,
    },
    pageHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: t.spacing.lg,
      flexWrap: 'wrap',
    },
    pageTitle: adminText.pageTitle,
    titleBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: t.spacing.xs,
    },
    pageDescription: adminText.secondary,
    cardList: {
      display: 'flex',
      flexDirection: 'column',
      gap: t.spacing.md,
    },
    card: {
      ...adminSurface.panel,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: t.spacing.lg,
      flexWrap: 'wrap',
      padding: `${t.spacing.lg} ${t.spacing.xl}`,
    },
    cardTitle: {
      margin: 0,
      fontSize: '15px',
      fontWeight: 700,
      color: t.text.primary,
    },
    cardSubtitle: {
      margin: `${t.spacing.xs} 0 0 0`,
      fontSize: t.typography.label.size,
      color: t.text.secondary,
    },
    cardActions: {
      display: 'flex',
      alignItems: 'center',
      gap: t.spacing.sm,
      flexShrink: 0,
    },
    primaryButton: adminButton.primary,
    addLessonButton: adminButton.primary,
    listToolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: t.spacing.md,
      flexWrap: 'wrap',
    },
    filterInput: {
      ...adminField.input,
      width: '320px',
      maxWidth: '100%',
    },
    resultsCount: {
      fontSize: t.typography.label.size,
      color: t.text.secondary,
      whiteSpace: 'nowrap',
    },
    resetFiltersButton: adminButton.link,
    panel: {
      ...adminSurface.panel,
      overflow: 'visible',
      minWidth: 0,
    },
    /**
     * Wraps a table inside `panel`: wide tables scroll here instead of pushing
     * the page sideways. Row menus use `AdminRowMenu` (fixed position) so this
     * clipping box never hides them.
     */
    tableScroll: {
      overflowX: 'auto',
      borderRadius: `calc(${t.radius.panel} - 1px)`,
    },
    stateText: {
      margin: 0,
      padding: t.spacing['2xl'],
      fontSize: t.typography.body.size,
      lineHeight: t.typography.body.lineHeight,
      color: t.text.secondary,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    headerCell: {
      padding: `${t.spacing.md} ${t.spacing.lg}`,
      borderBottom: `1px solid ${t.border.subtle}`,
      color: t.text.secondary,
      fontSize: t.typography.caption.size,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      textAlign: 'left',
      backgroundColor: t.surface.panel,
    },
    headerCellSortable: {
      cursor: 'pointer',
      userSelect: 'none',
      whiteSpace: 'nowrap',
    },
    headerCellLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: t.spacing.xs,
    },
    sortIndicator: {
      fontSize: '10px',
      lineHeight: 1,
    },
    sortIndicatorInactive: {
      opacity: 0.35,
    },
    headerCellActions: {
      width: '56px',
      padding: `${t.spacing.md} ${t.spacing.lg}`,
      borderBottom: `1px solid ${t.border.subtle}`,
      backgroundColor: t.surface.panel,
    },
    tableRow: {
      borderTop: `1px solid ${t.border.subtle}`,
    },
    idCell: {
      padding: `${t.spacing.md} ${t.spacing.lg}`,
      color: t.text.secondary,
      fontSize: t.typography.label.size,
      whiteSpace: 'nowrap',
    },
    bodyCell: {
      padding: `${t.spacing.md} ${t.spacing.lg}`,
      color: t.text.primary,
      fontSize: t.typography.body.size,
      verticalAlign: 'middle',
    },
    titleButton: {
      ...adminFocusable,
      padding: 0,
      border: 'none',
      backgroundColor: 'transparent',
      color: t.text.primary,
      fontFamily: 'inherit',
      fontSize: t.typography.body.size,
      fontWeight: 700,
      cursor: 'pointer',
      textAlign: 'left',
    },
    // NA-449: muted "Standard: <title>" line under a client-customized title.
    standardTitleLine: {
      marginTop: t.spacing.xs,
      color: t.text.secondary,
      fontSize: t.typography.caption.size,
      lineHeight: t.typography.caption.lineHeight,
    },
    accountLinkButton: {
      ...adminFocusable,
      padding: 0,
      border: 'none',
      backgroundColor: 'transparent',
      color: t.text.link,
      fontFamily: 'inherit',
      fontSize: t.typography.body.size,
      fontWeight: 600,
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      cursor: 'pointer',
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },
    actionsCell: {
      width: '56px',
      padding: `${t.spacing.sm} ${t.spacing.lg}`,
      textAlign: 'right',
      verticalAlign: 'middle',
      position: 'relative',
    },
    /** Inline text action in a row (e.g. Duplicate / Delete on Maps). */
    rowActionButton: {
      ...adminFocusable,
      minHeight: '32px',
      padding: `0 ${t.spacing.sm}`,
      border: 'none',
      borderRadius: t.radius.control,
      background: 'none',
      color: t.text.secondary,
      fontFamily: 'inherit',
      fontSize: t.typography.label.size,
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    rowActionButtonDanger: {
      color: t.status.error.text,
    },
    rowActions: {
      display: 'flex',
      gap: t.spacing.xs,
      justifyContent: 'flex-end',
    },
    actionMenuWrapper: {
      position: 'relative',
      display: 'inline-flex',
      justifyContent: 'flex-end',
    },
    actionMenuButton: {
      ...adminFocusable,
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      borderRadius: t.radius.control,
      border: `1px solid ${t.border.control}`,
      backgroundColor: t.surface.panel,
      color: t.text.secondary,
      cursor: 'pointer',
      fontSize: '16px',
      lineHeight: 1,
    },
    actionMenuPopover: {
      position: 'absolute',
      top: `calc(100% + ${t.spacing.sm})`,
      right: 0,
      minWidth: '168px',
      display: 'flex',
      flexDirection: 'column',
      padding: t.spacing.xs,
      border: `1px solid ${t.border.subtle}`,
      borderRadius: t.radius.tile,
      backgroundColor: t.surface.panel,
      boxShadow: t.shadow.overlay,
      zIndex: 5,
    },
    actionMenuItem: {
      ...adminFocusable,
      outlineOffset: '-2px',
      padding: `${t.spacing.sm} ${t.spacing.md}`,
      border: 'none',
      backgroundColor: 'transparent',
      color: t.text.primary,
      textAlign: 'left',
      fontFamily: 'inherit',
      fontSize: t.typography.body.size,
      borderRadius: t.radius.control,
      cursor: 'pointer',
    },
    actionMenuItemDisabled: {
      color: t.text.disabled,
      cursor: 'not-allowed',
    },
    actionMenuItemDanger: {
      color: t.status.error.text,
    },
    // NA-283: why a disabled Delete is disabled, rendered beneath the label so
    // the reason is visible, not just announced.
    actionMenuItemReason: {
      display: 'block',
      marginTop: '2px',
      fontSize: t.typography.caption.size,
      color: t.text.secondary,
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: t.surface.scrim,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: t.surface.panel,
      borderRadius: t.radius.panel,
      padding: t.spacing.xl,
      maxWidth: '420px',
      width: '90%',
      boxShadow: t.shadow.overlay,
      fontFamily: t.typography.fontFamily,
      color: t.text.primary,
    },
    modalTitle: {
      ...adminText.sectionTitle,
      margin: `0 0 ${t.spacing.md} 0`,
    },
    modalText: {
      margin: `0 0 ${t.spacing.lg} 0`,
      fontSize: t.typography.body.size,
      color: t.text.secondary,
      lineHeight: t.typography.body.lineHeight,
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: t.spacing.md,
      flexWrap: 'wrap',
    },
    /**
     * A disclosure chevron: reveals detail rather than performing an action, so it
     * carries no button fill that would compete with the real actions beside it.
     */
    disclosureButton: {
      ...adminFocusable,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      padding: 0,
      fontSize: '13px',
      color: t.text.secondary,
      backgroundColor: 'transparent',
      border: `1px solid ${t.border.control}`,
      borderRadius: t.radius.control,
      cursor: 'pointer',
    },
    cancelButton: adminButton.secondary,
    confirmDeleteButton: adminButton.danger,
    confirmActionButton: adminButton.primary,
    confirmDeleteButtonDisabled: adminButton.disabled,
  };
}
